import React, { useCallback, useEffect, useRef, useState } from "react";

import { CircularIndeterminate, FormButton } from "components";
import { FormikAutocomplete, FormikSelectInput, FormikTextInput } from "components/UI/Inputs/FormikInputs";

import { Form, Formik } from "formik";
import * as yup from "yup";

import { useRecoilValue, useSetRecoilState } from "recoil";
import {
    rowSelected,
    snackbarData,
    userCredentials,
} from "recoilState/GlobalState";

import { FORM_ERROR_MESSAGES } from "consts/errorMessages";

import styles from "styles/pages/ModalActions.module.scss";
import { handleResponse } from "services/Local-services";
import { accesoGet, horarioGet, usuarioLugarHorarioIns, zonaGet } from "services/services-mia/miakey";
import { Grid } from "@mui/material";

const FECHA_HOY = new Date().toISOString().substring(0, 10);

export default function InsertModal({ updateList, usuarioId, merchantServicioUsuarioId, activeGateLugarIds, closeModal }) {
    const [inRequest, setInRequest] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const [zonas, setZonas] = useState([]);
    const [accesos, setAccesos] = useState([]);
    const [accesosFiltrado, setAccesosFiltrado] = useState([]);
    const [horarios, setHorarios] = useState([]);

    const setSelected = useSetRecoilState(rowSelected);
    const setSnackbarInfo = useSetRecoilState(snackbarData);
    const credentials = useRecoilValue(userCredentials);

    const formikRef = useRef();

    const getAccesos = useCallback((zonaId) => {
        if (!zonaId) return;
        const zonaIdNum = Number(zonaId);
        const result = accesos.filter((a) => a.gateZonaId === zonaIdNum);
        setAccesosFiltrado(result);
        if (result[0]?.gateLugarId) selectAcceso(result[0].gateLugarId);
    }, [accesos]);

    useEffect(() => {
        const fetchData = async () => {
            setIsLoading(true);
            const { result: resultZonas } = await zonaGet({}, credentials);
            const { result: resultAccesos } = await accesoGet({}, credentials);
            const { result: resultHorarios } = await horarioGet({}, credentials);

            // -const availableAccesos = resultAccesos.filter((a) => !activeGateLugarIds.includes(a.gateLugarId));
            const availableAccesos = resultAccesos;
            setAccesos(availableAccesos);

            const availableZonaIds = new Set(availableAccesos.map((a) => a.gateZonaId));
            setZonas(resultZonas.filter((z) => availableZonaIds.has(z.gateZonaId)));

            setHorarios(resultHorarios);

            setIsLoading(false);
        };
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [credentials]);

    useEffect(() => {
        if (!zonas.length) return;
        selectZona(zonas[0].gateZonaId);
        getAccesos(zonas[0].gateZonaId);
    }, [getAccesos, zonas]);

    useEffect(() => {
        if (horarios.length > 0) selectHorario(horarios[0].gateHorarioId);
    }, [horarios]);

    const selectZona = (zonaId) => formikRef.current.setFieldValue("zona", zonaId);
    const selectAcceso = (accesoId) => formikRef.current.setFieldValue("acceso", accesoId);
    const selectHorario = (horarioId) => formikRef.current.setFieldValue("horario", horarioId);

    const handleChangeZona = (event) => {
        selectZona(event.gateZonaId);
        getAccesos(event.gateZonaId);
    };

    const handleChangeAcceso = (event) => {
        console.log(event)
        selectAcceso(event.gateLugarId)
    };

    const handleSubmit = async ({ zona, acceso, horario, fecha, estado }) => {
        setInRequest(true);

        const response = await usuarioLugarHorarioIns({
            usuarioId,
            merchantServicioUsuarioId,
            gateZonaId: zona,
            gateLugarId: acceso,
            gateHorarioId: horario,
            gateUsuarioLugarHorarioStatus: estado,
            gateUsuarioLugarHorarioFH: fecha,
        }, credentials);

        handleResponse(
            response,
            "Acceso agregado exitosamente",
            { setSnackbarInfo, setIsOpen: closeModal, setSelected, updateList }
        );

        setInRequest(false);
    };

    return (
        <Formik
            innerRef={formikRef}
            initialValues={{
                zona: "",
                acceso: "",
                horario: "",
                fecha: FECHA_HOY,
                estado: "A",
            }}
            validationSchema={yup.object({
                zona: yup.string(FORM_ERROR_MESSAGES.text).required(FORM_ERROR_MESSAGES.required),
                acceso: yup.string(FORM_ERROR_MESSAGES.text).required(FORM_ERROR_MESSAGES.required),
                horario: yup.string(FORM_ERROR_MESSAGES.text).required(FORM_ERROR_MESSAGES.required),
                fecha: yup.string(FORM_ERROR_MESSAGES.text).required(FORM_ERROR_MESSAGES.required),
                estado: yup.string(FORM_ERROR_MESSAGES.text).required(FORM_ERROR_MESSAGES.required),
            })}
            onSubmit={(values, { setSubmitting }) => {
                try {
                    handleSubmit(values);
                } catch (e) {
                    setSnackbarInfo({ message: e.errmsg, severity: "error", open: true });
                } finally {
                    setSubmitting(false);
                }
            }}
        >
            {(formik) => (
                <Form className={styles.inputs} noValidate>
                    <Grid container justifyContent={"center"} spacing={2}>

                        {zonas.length === 0 && !isLoading ? (
                            <Grid item xs={8}>
                                <p>No hay accesos disponibles para agregar.</p>
                            </Grid>
                        ) : (
                            <>
                                <Grid item md={6} lg={6}>
                                    <FormikAutocomplete
                                        name="zona"
                                        labelText="Zona"
                                        options={zonas}
                                        optionId="gateZonaId"
                                        optionLabel="gateZonaNombre"
                                        formik={formik}
                                        onChange={(e) => handleChangeZona(e)}
                                    />
                                </Grid>
                                <Grid item md={6} lg={6}>
                                    <FormikAutocomplete
                                        name="acceso"
                                        labelText="Acceso"
                                        options={accesosFiltrado}
                                        optionId="gateLugarId"
                                        optionLabel="gateLugarNombre"
                                        formik={formik}
                                        onChange={(e) => handleChangeAcceso(e)}
                                    />
                                </Grid>
                                <Grid item md={6} lg={6}>
                                    <FormikSelectInput name="horario" labelText="Horario">
                                        {horarios.map((h) => (
                                            <option key={h.gateHorarioId} value={h.gateHorarioId}>
                                                {h.gateHorarioNombre}
                                            </option>
                                        ))}
                                    </FormikSelectInput>
                                </Grid>
                                <Grid item md={3} lg={3}>
                                    <FormikTextInput
                                        name="fecha"
                                        labelText="Fecha desde"
                                        type="date"
                                        inputProps={{ min: FECHA_HOY }}
                                    />
                                </Grid>
                                <Grid item md={3} lg={3}>
                                    <FormikSelectInput name="estado" labelText="Estado">
                                        <option value="A">Alta</option>
                                        <option value="B">Baja</option>
                                    </FormikSelectInput>
                                </Grid>
                            </>
                        )}
                        <FormButton
                            inRequest={inRequest || isLoading}
                            newFormData={{ button: "Agregar" }}
                            onCloseModal={closeModal}
                        />
                        {(inRequest || isLoading) && <CircularIndeterminate />}
                    </Grid>
                </Form>
            )}
        </Formik>
    );
}
